'use client';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ChartCard } from '../../Cards/ChartCards/ChartCard';
import { CustomTooltip } from '../shared/CustomTooltip/CustomTooltip';
import styles from './DonutChart.module.css';
import { calculatePercentage, calculateTotal, enrichPieData } from '../../../utils/pieChartUtils';

export interface DonutChartData {
	name: string;
	value: number;
	percent?: number;
	color?: string;
	[key: string]: string | number | undefined;
}

export interface DonutChartProps {
	title: string;
	subtitle: string;
	buttonText: string;
	onButtonClick: () => void;
	data: DonutChartData[];
	emptyMessage?: string;
}

export const DonutChart = ({
	title,
	subtitle,
	buttonText,
	onButtonClick,
	data,
	emptyMessage,
}: DonutChartProps) => {
	const isEmpty = !data || data.length === 0;
	const total = isEmpty ? 0 : calculateTotal(data);
	const enrichedData = enrichPieData(data);

	return (
		<ChartCard
			title={title}
			subtitle={subtitle}
			buttonText={buttonText}
			onButtonClick={onButtonClick}
			isEmpty={isEmpty}
			emptyMessage={emptyMessage}
		>
			<div className={styles.chartContainer}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={enrichedData}
							cx="50%"
							cy="50%"
							innerRadius="55%"
							outerRadius="80%"
							paddingAngle={0}
							dataKey="value"
							startAngle={90}
							endAngle={-270}
							stroke="none"
						>
							{enrichedData.map((entry) => (
								<Cell key={`cell-${entry.name}`} fill={entry.fill} />
							))}
						</Pie>
						<Tooltip
							content={<CustomTooltip subtitle={subtitle} variant="donut" />}
							cursor={false}
							allowEscapeViewBox={{ x: true, y: true }}
							wrapperStyle={{ outline: 'none', zIndex: 1000 }}
						/>
					</PieChart>
				</ResponsiveContainer>
			</div>

			<div className={styles.legend}>
				{enrichedData.map((item) => {
					const percentage = item.percent ?? calculatePercentage(item.value, total);
					return (
						<div key={`legend-${item.name}`} className={styles.legendItem}>
							<div className={styles.legendDotContainer}>
								{' '}
								<div className={styles.legendDot} style={{ backgroundColor: item.fill }} />
							</div>{' '}
							<div className={styles.legendText}>
								<span className={styles.legendLabel}>{item.name}</span>
								<span className={styles.legendPercentage}>{percentage}%</span>
							</div>
						</div>
					);
				})}
			</div>
		</ChartCard>
	);
};
